import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';
import { ActiveCallOverlaySaveEvent } from 'src/app/shared/components/active-call-overlay/active-call-overlay.models';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { ActiveCallOverlayService } from 'src/app/shared/services/active-call-overlay.service';
import { ConnectionsDataService } from 'src/app/view/connections/services/connections-data.service';
import { ConnectionsService } from 'src/app/view/connections/services/connections.service';
import { ResidentConnectionVM } from 'src/app/view/connections/services/models/connections.view-model';

const CONNECTION_WAIT_TIMEOUT_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class ConnectionActiveCallOverlayService {
  private readonly subscription$ = new Subscription();

  constructor(
    private activeCallOverlayService: ActiveCallOverlayService,
    private connectionsDataService: ConnectionsDataService,
    private connectionsService: ConnectionsService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.subscription$.add(
      this.activeCallOverlayService.saveRequested$.subscribe((event) => {
        void this.handleActiveCallSave(event);
      })
    );
  }

  public canOpen(connection: ResidentConnectionVM): boolean {
    return connection.isOngoing;
  }

  public open(connection: ResidentConnectionVM): void {
    if (!this.canOpen(connection)) return;

    this.activeCallOverlayService.open({
      id: connection.id,
      contactName: connection.residentName,
      phoneNumber: connection.residentPhone
    });
  }

  /** Opens the overlay only after RTDB confirms that the call is still active. */
  public async openCall(connectionId: string): Promise<boolean> {
    try {
      const connection = await this.waitForConnection(connectionId);
      if (!this.canOpen(connection)) return false;

      this.open(connection);
      return true;
    } catch {
      return false;
    }
  }

  private async handleActiveCallSave(event: ActiveCallOverlaySaveEvent): Promise<void> {
    const content = event.note.trim();
    if (!content.length) {
      this.activeCallOverlayService.patch({ note: '' });
      return;
    }

    this.activeCallOverlayService.setSaving(true);
    try {
      if (!event.data.id) throw new Error('Connection id is missing');

      const connection = await this.waitForConnection(event.data.id);
      await this.connectionsService.saveNote(connection.id, content);
      this.activeCallOverlayService.patch({ note: '' });
    } catch (error) {
      this.errorHandlingService.showGlobalError('error.noteAdd', error);
    } finally {
      this.activeCallOverlayService.setSaving(false);
    }
  }

  private async waitForConnection(connectionId: string): Promise<ResidentConnectionVM> {
    const connection = await this.connectionsDataService
      .getConnections$()
      .pipe(
        map((connections) => connections.find((item) => item.id === connectionId)),
        filter((item): item is ResidentConnectionVM => !!item),
        take(1),
        timeout(CONNECTION_WAIT_TIMEOUT_MS)
      )
      .toPromise();

    if (!connection) throw new Error(`Connection was not found: ${connectionId}`);
    return connection;
  }
}
